import type { FaqCategory } from "@/lib/faqs-data";
import type { Locale } from "@/lib/i18n";
import { faqsByCategory } from "@/lib/faqs-data";

const faqsByCategoryFr: FaqCategory[] = [
  {
    category: "Applications",
    items: [
      {
        q: "Comment savoir quelle application me convient ?",
        a: "Commencez par le problème à résoudre — formulaires, événements ou un flux interne très précis. Les formulaires conformes HIPAA conviennent à la collecte de renseignements de santé protégés, Event Maker aux inscriptions et billets, Rubric à la notation structurée, et les applications sur mesure lorsque rien d'autre ne convient vraiment.",
      },
      {
        q: "Les applications peuvent-elles s'intégrer à mes outils existants ?",
        a: "Oui. La plupart des applications s'intègrent aux CRM, outils marketing et systèmes administratifs courants via webhooks, API ou un léger middleware. Pendant l'intégration, nous examinerons votre stack et préciserons ce qui est possible et ce qui exige du travail sur mesure.",
      },
      {
        q: "Pouvez-vous personnaliser une application pour mon entreprise ?",
        a: "Oui. Pour beaucoup de clients, nous partons d'une application existante comme les formulaires HIPAA ou Event Maker, puis nous ajoutons des flux, champs ou intégrations personnalisés pour coller à la façon dont votre équipe travaille vraiment.",
      },
      {
        q: "Les applications conviennent-elles aux petites équipes et à celles qui grandissent ?",
        a: "Les applications sont conçues pour de très petites équipes tout en soutenant la croissance — ajout d'utilisateurs, de rôles et d'environnements à mesure que vous évoluez. Nous pouvons discuter de où vous en êtes et de où vous voulez être dans 12 à 24 mois.",
      },
      {
        q: "À quoi ressemble le processus d'intégration d'une nouvelle application ?",
        a: "Nous commençons par un court appel de découverte, cartographions vos flux, configurons un environnement de test et vous guidons dans un pilote. Une fois que tout est bon, nous convenons d'un plan de mise en ligne, de formation et d'une documentation de base.",
      },
      {
        q: "Comment les bogues ou demandes de fonctionnalités sont-ils traités ?",
        a: "Les bogues et demandes sont suivis dans une file partagée. Nous priorisons d'abord ce qui touche la stabilité ou la conformité, puis les améliorations de confort. Les demandes plus importantes sont estimées et planifiées avec vous.",
      },
    ],
  },
  {
    category: "Soutien web",
    items: [
      {
        q: "Que couvre généralement le soutien web ?",
        a: "Le soutien web inclut habituellement les mises à jour, le dépannage, l'optimisation des performances, le durcissement de la sécurité et l'aide aux déploiements pour les sites WordPress, Next.js et Joomla. Nous aidons aussi pour le DNS, le SSL, le routage du courriel et les panneaux de contrôle lorsqu'ils affectent votre site.",
      },
      {
        q: "À quelle vitesse pouvez-vous répondre aux problèmes ?",
        a: "La plupart des billets non urgents reçoivent une première réponse pendant les heures d'ouverture, et les plans prioritaires offrent des délais plus courts ainsi qu'un chemin d'escalade clair pour les pannes. Pour les incidents critiques, nous définissons avec vous les niveaux de gravité et qui est avisé.",
      },
      {
        q: "Pouvez-vous aider si quelqu'un d'autre a bâti notre site ?",
        a: "Oui. Une grande partie de notre travail consiste à soutenir ou stabiliser des sites bâtis par d'autres agences ou pigistes. Nous commençons par une brève évaluation pour comprendre la stack, puis un court plan pour les mises à jour, correctifs et la surveillance.",
      },
      {
        q: "Traitez-vous des correctifs ponctuels ou seulement des forfaits ?",
        a: "Les deux. Beaucoup de clients commencent par un incident ponctuel ou un petit projet, puis passent à un forfait récurrent une fois qu'ils voient la valeur d'avoir quelqu'un d'astreinte qui connaît déjà leur environnement.",
      },
      {
        q: "Pouvez-vous coordonner avec notre TI ou marketing existant ?",
        a: "Oui. Nous travaillons souvent aux côtés de la TI interne, du marketing ou d'agences externes. Nous clarifions qui possède quels systèmes, puis documentons un chemin de communication simple pour que les problèmes ne tombent pas entre les équipes.",
      },
      {
        q: "De quelles informations avez-vous besoin pour commencer à soutenir notre site ?",
        a: "Typiquement, l'accès à votre panneau d'hébergement, au dépôt de code (si vous en avez un) et aux services tiers qui touchent le site — DNS, fournisseur SSL ou livraison de courriel. Nous documentons cela pendant l'intégration pour que le travail futur soit rapide et prévisible.",
      },
    ],
  },
  {
    category: "Hébergement",
    items: [
      {
        q: "Qu'est-ce qui distingue l'hébergement CloudAxe ?",
        a: "L'hébergement CloudAxe mise sur une stack propre et bien comprise, avec surveillance, sauvegardes et tests de restauration intégrés. Nous optimisons pour les sites de petites entreprises qui ont besoin de fiabilité et d'une communication claire, pas d'infrastructure expérimentale.",
      },
      {
        q: "Comment fonctionnent les sauvegardes et les tests de restauration ?",
        a: "Les sauvegardes sont prises selon un horaire et stockées hors serveur, et nous effectuons périodiquement des tests de restauration pour savoir qu'elles fonctionnent en pratique, pas seulement en théorie. Si vous avez besoin d'une restauration, nous avons déjà un chemin testé pour remettre votre site en ligne.",
      },
      {
        q: "Pouvez-vous nous migrer depuis notre hébergeur actuel ?",
        a: "Oui. Nous gérons le plan de migration complet — clonage du site, synchronisation des bases et fichiers, DNS et SSL, et planification du basculement final. L'objectif est de minimiser les temps d'arrêt et d'éviter les surprises.",
      },
      {
        q: "Soutenez-vous les environnements de préproduction ?",
        a: "Oui. Pour la plupart des sites, nous recommandons un environnement de préproduction où tester en toute sécurité les mises à jour, nouvelles fonctionnalités et changements de configuration avant la production.",
      },
      {
        q: "Comment surveillez-vous la disponibilité et la performance ?",
        a: "Nous utilisons une surveillance qui vérifie la disponibilité, des indicateurs de santé clés et la performance de base. Quand quelque chose semble anormal, nous enquêtons et traitons directement ou coordonnons avec vous, selon votre plan.",
      },
      {
        q: "Que se passe-t-il si notre trafic explose soudainement ?",
        a: "Si vous êtes sur un plan d'hébergement géré, nous examinons les options de montée en capacité, la mise en cache et la configuration CDN pour garder le site stable. Nous analysons aussi la cause du pic pour dimensionner correctement l'environnement par la suite.",
      },
    ],
  },
];

export function getFaqsByCategory(locale: Locale): FaqCategory[] {
  return locale === "fr" ? faqsByCategoryFr : faqsByCategory;
}

export function flattenFaqsForJsonLd(locale: Locale = "en") {
  return getFaqsByCategory(locale).flatMap((group) =>
    group.items.map((item) => ({
      "@type": "Question" as const,
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer" as const,
        text: item.a,
      },
    }))
  );
}
